import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, LeafIcon } from 'lucide-react';
import { ShopFaq } from '../shared/ShopFaq';
import { ImageSlot } from '../shared/ImageSlot';
import { GiftCardBlock } from '../shared/GiftCardBlock';
import { HeadingText } from '../shared/HeadingText';
import { RoutineCard } from '../shared/RoutineCard';
import { SubscriptionBlock } from '../shared/SubscriptionBlock';
import { TrustBadges } from '../shared/TrustBadges';
import { Drawer } from '../shared/Drawer';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { pageActions } from '../../data/pageActions';
import { usePages } from '../../contexts/SiteContext';
import { founder } from '../../data/content';
import { listNl, plural } from '../../core/format/text';
import { fill } from '../../utils/copy';
import { btnPrimary, btnPrimaryLg, btnSecondary, btnSecondaryLg } from '../../ui/styles';
import { heroTitle, quietLink, sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { FilterPanel } from './ShopFilters';
import { COLLECTION_ID, ShopCollection } from './ShopCollection';
import { IngredientIndex } from './IngredientIndex';
import { ROUTINES_SHOWN } from './shopRules';
import { placeholderImages } from '../../data/placeholderImages';
import { mediaOr } from '../../core/rules/media';
import { useShopFilters } from './useShopFilters';

/**
 * De webshop (docs/VORMGEVING.md › Pagina's). Vraag: wat past bij mijn huid, zonder te gokken?
 * Hoofdknop "Kies op je huidvraag" (naar de collectie met huidfilters), het huidadvies als tweede.
 * kop + collectie (sand) · groot beeld (product in gebruik) · beloftes · protocollen (sand) ·
 * wat er écht in zit (shell) · abonnement (klei, het ene moment) · merken (shell) · vragen (sand) · cadeaubon (blush).
 */
export function Shop() {
  const pages = usePages();
  const {
    openQuiz
  } = useOverlayActions();
  const {
    products,
    brands,
    routines
  } = useCatalog();
  const f = useShopFilters();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const copy = pages.shop;
  const vars = {
    count: plural(products.length, 'product', 'producten'),
    brands: listNl(brands.map(b => b.name))
  };
  return <>
      {/* Compacte kop: wie hier komt, wil producten zien. Het verhaal staat verderop. */}
      <section className="bg-sand" data-id="element-6783" data-mp="3byk323ibcwnu">
        <div className="mx-auto flex max-w-content flex-col gap-5 px-6 pb-2 pt-10 lg:flex-row lg:items-end lg:justify-between lg:gap-12 lg:px-10 lg:pt-16" data-id="element-6784" data-mp="36xcq7q1bd7ab">
          <div className="max-w-3xl" data-id="element-6785" data-mp="324jlh25uhyy8">
            <h1 className={heroTitle} data-id="element-6786" data-mp="2y3zt0bw7vjif">
              <HeadingText heading={copy.hero.heading} data-id="element-6787" data-mp="2y5124ato9vxi" />
            </h1>
            {copy.hero.body && <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-muted" data-id="element-6788" data-mp="2t799fel7e5s9">{fill(copy.hero.body, vars)}</p>}
          </div>
          {/* Hoofdknop: kiezen op je huid (de collectie met de huidfilters). Twijfel? Het huidadvies, persoonlijk met haar gezicht. */}
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row lg:self-end" data-id="element-6789" data-mp="294d4ens9bphl">
            <a href={`#${COLLECTION_ID}`} className={btnPrimaryLg} data-id="element-6790" data-mp="2e2eqziow66xt">
              {pageActions.chooseByConcern}
            </a>
            {copy.hero.secondaryCta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={`${btnSecondaryLg} group pl-2 text-left`} data-id="element-6791" data-mp="2izwyr5r6ag48">
                <img src={founder.portrait} alt="" width={36} height={36} className="h-9 w-9 shrink-0 rounded-full object-cover object-top" data-id="element-6792" data-mp="1qdlyxxbe4j8h" />
                <span className="leading-snug" data-id="element-6793" data-mp="1vcoxcywv7r7h">{fill(copy.hero.secondaryCta)}</span>
                <ArrowRightIcon className="h-4 w-4 shrink-0 transition-transform duration-150 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6794" data-mp="20ahz9fr4xz1y" />
              </button>}
          </div>
        </div>
        {/* Vertrouwen, geen tweede knop: de salon als bron van de keuze. */}
        {copy.proof && <p className="mx-auto max-w-content px-6 pt-4 text-[14px] text-muted lg:px-10" data-id="element-6795" data-mp="1cjky8y0vom5h">{fill(copy.proof)}</p>}
      </section>

      <ShopCollection f={f} onOpenFilters={() => setFiltersOpen(true)} data-id="element-6796" data-mp="39brxr1jzt95f" />

      {/* Het grote beeld van de webshop: een product in gebruik (docs/BEELDEN.md, W1). Tussen collectie en beloftes. */}
      <div className="bg-sand" data-id="element-6797" data-mp="3j9y1zpt9urdg">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-6798" data-mp="3j8wpdgwlxwtm">
          <ImageSlot code="W1" media={mediaOr(copy.inUse.media, {
          image: placeholderImages.shopInUse,
          alt: 'Een druppel serum op een vingertop, boven een open flesje'
        })} className="aspect-[4/3] w-full sm:aspect-[21/9]" sizes="(min-width: 1280px) 1200px, 100vw" data-id="element-6799" data-mp="vyi0oxhk1pf" />
        </div>
      </div>

      {/* Zekerheden ná de collectie: ze bevestigen de keuze, maar staan het kiezen niet in de weg. */}
      <TrustBadges data-id="element-6800" data-mp="2ckpsgiju9dz0" />

      {/* Protocollen direct na de collectie: wie twijfelt tussen losse producten, krijgt hier het complete plan. */}
      {routines.length > 0 && <Section tone="sand" labelledBy="shop-routines" contained={false} data-id="element-6801" data-mp="n4dtqf04m6ql">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-6802" data-mp="n6go1b8bam14">
            <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-6803" data-mp="s25hqxxnh8pi">
              <div className="max-w-2xl" data-id="element-6804" data-mp="dfcbei63zd9g">
                <h2 id="shop-routines" className={`${sectionHeading} text-ink`} data-id="element-6805" data-mp="deaongfqd2bw">
                  <HeadingText heading={copy.routines.heading} data-id="element-6806" data-mp="iccf4mfhk3xe" />
                </h2>
                {copy.routines.body && <p className="mt-5 text-[17px] leading-relaxed text-muted" data-id="element-6807" data-mp="4cb80p8mo09h">{fill(copy.routines.body)}</p>}
              </div>
              {copy.routines.cta && <Link to="/routines" className={`${quietLink} text-ink`} data-id="element-6808" data-mp="3qlvxobcs2tlq">
                  {fill(copy.routines.cta)}
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6809" data-mp="3vkyr0yjebbdv" />
                </Link>}
            </div>

            <ul className="mt-14 grid gap-6 lg:grid-cols-3" data-id="element-6810" data-mp="1tsu2zy50oy8f">
              {routines.slice(0, ROUTINES_SHOWN).map(r => <li key={r.id} className="h-full bg-shell p-7 lg:p-8" data-id="element-6811" data-mp="1sc6lrk7kpnzs">
                  <RoutineCard routine={r} data-id="element-6812" data-mp="1nck510sjdpm8" />
                </li>)}
            </ul>
          </div>
        </Section>}

      {/* Wat er écht in zit: voor wie wil weten wáárom iets werkt. */}
      <IngredientIndex data-id="element-6813" data-mp="1ec5hrvw0i0g3" />

      <SubscriptionBlock data-id="element-6814" data-mp="1ecnk9a99pawg" />

      {/* Merken */}
      <Section tone="shell" labelledBy="shop-brands" contained={false} data-id="element-6815" data-mp="1edyofb6mkko3">
        <div className="mx-auto grid max-w-content gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-6816" data-mp="2b1mxl3opl2vi">
          <div className="lg:col-span-5" data-id="element-6817" data-mp="263ldqgjgkziz">
            <h2 id="shop-brands" className={`${sectionHeading} text-ink`} data-id="element-6818" data-mp="21cbnpetzzu6k">
              {fill(copy.brands.heading)}
            </h2>
            <p className="mt-6 flex items-start gap-3 text-[17px] leading-relaxed text-muted" data-id="element-6819" data-mp="1x9ow9f18ob9w">
              <LeafIcon className="mt-1.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6820" data-mp="2tto3ur3tbvey" />
              {/* De boomclaim alleen zolang emerginC echt in het assortiment zit. */}
              {fill(copy.brands.body)}
              {brands.some(b => b.name === 'emerginC') && ' emerginC plant bovendien een boom voor elk verkocht product.'}
            </p>
          </div>
          <ul className="grid gap-10 sm:grid-cols-2 lg:col-span-7" data-id="element-6821" data-mp="kjopqt8erys7">
            {brands.map(b => <li key={b.name} className="border-t border-ink/15 pt-6" data-id="element-6822" data-mp="pjb1szci38zf">
                <h3 className="type-title text-ink" data-id="element-6823" data-mp="uidxht6lc2uq">{b.name}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-6824" data-mp="uhvumdpfaqdu">{b.description}</p>
              </li>)}
          </ul>
        </div>
      </Section>

      <ShopFaq data-id="element-6825" data-mp="14908fa3d4573" />

      <GiftCardBlock data-id="element-6826" data-mp="148gzujzlqt5q" />

      <Drawer open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filters" footer={
    // Wissen zo smal als zijn woord, de hoofdknop krijgt de rest: zo past "Toon 43 producten"
    // ook op een telefoon van 320px, zonder afkappen of horizontaal scrollen.
    <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-3" data-id="element-6828" data-mp="t4kvg07m2h84">
            <button type="button" onClick={f.clear} disabled={!f.hasFilters} className={`${btnSecondary} px-4`} data-id="element-6829" data-mp="t6nsh476lamc">
              Wissen
            </button>
            {/* Bij nul resultaten niet "Toon 0 producten": dan is er niets te tonen. */}
            <button type="button" onClick={() => setFiltersOpen(false)} disabled={f.visible.length === 0} className={`${btnPrimary} min-w-0 px-4`} data-id="element-6830" data-mp="y34iy51us12l">
              {f.visible.length === 0 ? 'Geen producten' : `Toon ${plural(f.visible.length, 'product', 'producten')}`}
            </button>
          </div>} data-id="element-6827" data-mp="14aba94d8z2vk">
        <FilterPanel skin={f.skin} setSkin={f.setSkin} step={f.step} setStep={f.setStep} brand={f.brand} setBrand={f.setBrand} showSkin={false} data-id="element-6831" data-mp="j7ezoeo1y21k" />
      </Drawer>
    </>;
}